import React, { useEffect, useRef, useState, useContext } from 'react';
import { motion } from 'framer-motion';
import { useTheme, ThemeContext } from '../context/ThemeContext';

const ThemeTransition = ({ children }) => {
  const { theme, transitionState, completeTransition } = useTheme();
  const containerRef = useRef(null);
  const duplicateRef = useRef(null);
  const [showOverlay, setShowOverlay] = useState(false);
  const [animationProps, setAnimationProps] = useState(null);

  useEffect(() => {
    if (transitionState.isTransitioning && transitionState.clickPosition) {
      const { x, y } = transitionState.clickPosition;
      const width = window.innerWidth;
      const height = window.innerHeight;
      
      // Calculate distance to farthest corner
      const distances = [
        Math.sqrt(x * x + y * y),
        Math.sqrt((width - x) ** 2 + y ** 2),
        Math.sqrt(x ** 2 + (height - y) ** 2),
        Math.sqrt((width - x) ** 2 + (height - y) ** 2),
      ];
      
      const maxRadius = Math.max(...distances) * 1.5;

      setAnimationProps({
        x,
        y,
        maxRadius,
        nextTheme: transitionState.nextTheme,
      });

      // Show overlay on next frame to ensure DOM is ready
      requestAnimationFrame(() => {
        setShowOverlay(true);
        
        // Sync scroll position
        requestAnimationFrame(() => {
          if (duplicateRef.current) {
            duplicateRef.current.scrollTop = window.scrollY;
          }
        });
      });

      // Complete transition after animation
      const timer = setTimeout(() => {
        setShowOverlay(false);
        completeTransition();
        setAnimationProps(null);
      }, 900);

      return () => clearTimeout(timer);
    } else {
      setShowOverlay(false);
      setAnimationProps(null);
    }
  }, [transitionState, completeTransition]);

  return (
    <div ref={containerRef} className="relative" data-theme={theme}>
      {/* Original content */}
      {children}

      {/* Animated overlay with new theme */}
      {showOverlay && animationProps && (
        <motion.div
            className="fixed inset-0 z-[9999] pointer-events-none"
            data-theme={animationProps.nextTheme}
            initial={{
              clipPath: `circle(0px at ${animationProps.x}px ${animationProps.y}px)`,
            }}
            animate={{
              clipPath: `circle(${animationProps.maxRadius}px at ${animationProps.x}px ${animationProps.y}px)`,
            }}
            transition={{
              duration: 0.8,
              ease: [0.16, 1, 0.3, 1],
            }}
            style={{
              width: '100vw',
              height: '100vh',
              willChange: 'clip-path',
              transform: 'translateZ(0)',
            }}
          >
            <div 
              ref={duplicateRef}
              className="w-full h-full overflow-auto"
              style={{
                transform: 'translateZ(0)',
              }}
            >
              <ThemeContextOverride theme={animationProps.nextTheme}>
                {children}
              </ThemeContextOverride>
            </div>
          </motion.div>
      )}
    </div>
  );
};

// Component to override theme context for overlay children
const ThemeContextOverride = ({ children, theme }) => {
  const originalContext = useContext(ThemeContext);
  
  return (
    <ThemeContext.Provider
      value={{
        ...originalContext,
        theme: theme,
      }}
    >
      {children}
    </ThemeContext.Provider>
  );
};

export default ThemeTransition;
